import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { RestaurantProvider } from "./contexts/RestaurantContext";
import { SubscriptionProvider } from "./components/PlanGate";
import PlanGate from "./components/PlanGate";
import InstallPrompt from "./components/InstallPrompt";
import { registerServiceWorker } from "./lib/pwa";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import FloorPlanPage from "./pages/FloorPlanPage";
import WaitlistPage from "./pages/WaitlistPage";
import WaitlistStatusPage from "./pages/WaitlistStatusPage";
import ReservationsPage from "./pages/ReservationsPage";
import WalkInsPage from "./pages/WalkInsPage";
import ServersPage from "./pages/ServersPage";
import EventsPage from "./pages/EventsPage";
import SectionsPage from "./pages/SectionsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import SettingsPage from "./pages/SettingsPage";
import DataPage from "./pages/DataPage";
import ServerPerformancePage from "./pages/ServerPerformancePage";
import MembersPage from "./pages/MembersPage";
import SalesPage from "./pages/SalesPage";
import WalkthroughPage from "./pages/WalkthroughPage";
import ServerDashboardPage from "./pages/ServerDashboardPage";
import ChatPage from "./pages/ChatPage";
import NotificationsPage from "./pages/NotificationsPage";
import EndOfServicePage from "./pages/EndOfServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import AccountingPage from "./pages/AccountingPage";
import InventoryPage from "./pages/InventoryPage";
import BillingPage from "./pages/BillingPage";

function ServerRedirect({ children }) {
  const { user } = useAuth();
  if (user?.role === "server") return <Navigate to="/my-section" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function ProtectedRouteOrLanding({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F9F9F7" }}>
        <div className="w-8 h-8 border-2 border-stone-300 border-t-[#2F4F4F] rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <LandingPage />;
  return children;
}

function App() {
  useEffect(() => { registerServiceWorker(); }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/waitlist/status/:id" element={<WaitlistStatusPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route element={<ProtectedRouteOrLanding><RestaurantProvider><SubscriptionProvider><Layout /></SubscriptionProvider></RestaurantProvider></ProtectedRouteOrLanding>}>
            <Route path="/" element={<ServerRedirect><FloorPlanPage /></ServerRedirect>} />
            <Route path="/waitlist" element={<WaitlistPage />} />
            <Route path="/reservations" element={<ReservationsPage />} />
            <Route path="/walk-ins" element={<WalkInsPage />} />
            <Route path="/servers" element={<ServersPage />} />
            <Route path="/sections" element={<SectionsPage />} />
            <Route path="/events" element={<PlanGate feature="events"><EventsPage /></PlanGate>} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/data" element={<DataPage />} />
            <Route path="/server-performance" element={<ServerPerformancePage />} />
            <Route path="/members" element={<PlanGate feature="members"><MembersPage /></PlanGate>} />
            <Route path="/sales" element={<PlanGate feature="sales"><SalesPage /></PlanGate>} />
            <Route path="/walkthrough" element={<WalkthroughPage />} />
            <Route path="/my-section" element={<ServerDashboardPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/end-of-service" element={<PlanGate feature="end_of_service"><EndOfServicePage /></PlanGate>} />
            <Route path="/accounting" element={<PlanGate feature="accounting"><AccountingPage /></PlanGate>} />
            <Route path="/inventory" element={<PlanGate feature="inventory"><InventoryPage /></PlanGate>} />
            <Route path="/billing" element={<BillingPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" />
      <InstallPrompt />
    </AuthProvider>
  );
}

export default App;
